import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { sanitizeUser } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserRaw] = useState(null);
  const [loading, setLoading] = useState(true);

  // Guarded setter: any caller feeding non-user data (DOM elements, event
  // objects, HTML strings from a leaked fallback) is silently coerced to
  // null instead of poisoning RBAC state.
  const setUser = useCallback((candidate) => {
    setUserRaw(sanitizeUser(candidate));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/me")
      .then((res) => { if (!cancelled) setUserRaw(sanitizeUser(res.data)); })
      .catch(() => { if (!cancelled) setUserRaw(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const clean = sanitizeUser(res.data);
    setUserRaw(clean);
    if (!clean) throw new Error("Login response was malformed");
    return clean;
  };

  const register = async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    const clean = sanitizeUser(res.data);
    setUserRaw(clean);
    if (!clean) throw new Error("Registration response was malformed");
    return clean;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUserRaw(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setUser }),
    [user, loading, setUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
